import { useCallback, useEffect, useRef } from 'react'
import type { SessionCommand } from '@fitting-room/contracts'

/**
 * What she has typed and not yet sent.
 *
 * The questionnaire is long, it is read on a phone, and a phone takes calls.
 * Component state does not survive the tab being evicted, so every typed screen
 * keeps a local copy of her keystrokes until the server has taken them.
 *
 * Three rules hold this together and none of them is optional:
 *
 * 1. A draft is unsent input, never a second source of truth. It opens a field
 *    that has not been sent yet; the moment its command is accepted the draft
 *    goes, so a correction the boutique makes on the pull sheet is what she
 *    sees when she comes back — not what she typed an hour ago.
 * 2. `''` is a value. She cleared the field. A store that answers "nothing
 *    here" for a cleared field puts back what she just deleted, which is the
 *    one bug this file exists to not have.
 * 3. Everything under a token goes when that session is submitted. The phone
 *    may be borrowed, and a name, a phone number and a set of measurements
 *    must not outlive the appointment in a browser's storage.
 *
 * Storage failing is never the customer's problem: private windows, cleared
 * site data and browsers set to block storage all throw or return nothing, and
 * every path here answers that with "she has no draft" and carries on.
 */

/** Namespaced like `fitting-room.recent-session-tokens`, one record per session. */
const KEY_PREFIX = 'fitting-room.draft.'

const storageKey = (token: string): string => `${KEY_PREFIX}${token}`

/** The intake screen's drafts, one field per key in the step's own field list. */
export const intakeScope = (stepId: string): string => `intake:${stepId}`

/** The summary's note editor: one draft per pick, keyed by its garment id. */
export const NOTE_SCOPE = 'note'

/**
 * The three methods of `localStorage` used here, and nothing more.
 *
 * Declared rather than borrowed from the DOM's `Storage` so a caller — a test,
 * or a surface that one day wants `sessionStorage` — can hand over anything of
 * this shape, and so nothing in this module needs a browser to be exercised.
 */
export interface DraftStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/**
 * `localStorage` where there is one.
 *
 * Reaching for the property is itself inside the `try`: a browser set to block
 * storage throws on the access, not on the call.
 */
export function browserStorage(): DraftStorage | null {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch {
    return null
  }
}

/** `{ scope: { field: value } }` — see `read` for why the nesting earns its keep. */
type DraftRecord = Record<string, Record<string, string>>

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

function readRecord(storage: DraftStorage, key: string): DraftRecord {
  try {
    const raw = storage.getItem(key)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (!isRecord(parsed)) return {}
    const record: DraftRecord = {}
    for (const [scope, fields] of Object.entries(parsed)) {
      if (!isRecord(fields)) continue
      const kept: Record<string, string> = {}
      for (const [field, value] of Object.entries(fields)) {
        // Anything that is not a string was not written by this file, and a
        // number read back as a value would render as one in a text input.
        if (typeof value === 'string') kept[field] = value
      }
      record[scope] = kept
    }
    return record
  } catch {
    return {}
  }
}

function writeRecord(storage: DraftStorage, key: string, record: DraftRecord): void {
  try {
    const empty = Object.values(record).every((fields) => Object.keys(fields).length === 0)
    if (empty) storage.removeItem(key)
    else storage.setItem(key, JSON.stringify(record))
  } catch {
    /* Full, blocked or private: she keeps typing and the screen says nothing. */
  }
}

export interface DraftStore {
  /** `null` when she has not typed here, `''` when she cleared the field. */
  read(scope: string, field: string): string | null
  readScope(scope: string): Record<string, string>
  /** Merges. Only the fields passed are touched, so an untouched field stays unwritten. */
  write(scope: string, fields: Readonly<Record<string, string>>): void
  clearField(scope: string, field: string): void
  clearScope(scope: string): void
  clearAll(): void
}

/**
 * Every draft for one session, under one key.
 *
 * One record per token rather than one per field, because "clear everything for
 * this session" is then a single `removeItem` that either happens or does not.
 * A key-per-field scheme would clear by scanning and enumerating the whole
 * storage, and a scan that throws halfway leaves half of her details behind on
 * a phone that may not be hers — which is exactly the failure this must not
 * have. The cost is that two tabs open on the SAME session write the whole
 * record each, so the last one wins; two different sessions never collide,
 * which is the case the acceptance criteria name.
 */
export function draftStore(token: string, storage: DraftStorage | null = browserStorage()): DraftStore {
  // No token is no session: the dev index can render `/s/` with an empty one.
  const usable = token ? storage : null
  const key = storageKey(token)

  const load = (): DraftRecord => (usable ? readRecord(usable, key) : {})
  const save = (record: DraftRecord): void => {
    if (usable) writeRecord(usable, key, record)
  }

  return {
    read(scope, field) {
      const fields = load()[scope]
      // `hasOwn` rather than a truthiness test, because `''` is the whole
      // point — and rather than `!== undefined`, so a field named after
      // something on `Object.prototype` cannot answer with a function.
      if (!fields || !Object.hasOwn(fields, field)) return null
      return fields[field] ?? null
    },
    readScope(scope) {
      return { ...(load()[scope] ?? {}) }
    },
    write(scope, fields) {
      if (!usable || Object.keys(fields).length === 0) return
      const record = load()
      record[scope] = { ...record[scope], ...fields }
      save(record)
    },
    clearField(scope, field) {
      if (!usable) return
      const record = load()
      const current = record[scope]
      if (!current || !Object.hasOwn(current, field)) return
      const next = { ...current }
      delete next[field]
      if (Object.keys(next).length === 0) delete record[scope]
      else record[scope] = next
      save(record)
    },
    clearScope(scope) {
      if (!usable) return
      const record = load()
      if (!Object.hasOwn(record, scope)) return
      delete record[scope]
      save(record)
    },
    clearAll() {
      if (!usable) return
      try {
        usable.removeItem(key)
      } catch {
        /* Nothing else to try, and nothing to tell her. */
      }
    },
  }
}

/**
 * Retire the drafts behind the commands the server has just accepted.
 *
 * Called from the one place that knows a command landed rather than from the
 * screens that send them: after the response the session holds the value, and
 * a draft still sitting in front of it would shadow anything anyone else
 * changed. A failed send clears nothing, which is the whole point of having
 * written the draft in the first place.
 *
 * `submit` is deliberately not handled here. Clearing the session's drafts
 * hangs on `session.status` in `SessionRoute` instead, because it must also
 * fire for a link she opens again tomorrow, on a session that was already
 * submitted before this tab existed.
 */
export function clearAcceptedDrafts(
  token: string,
  commands: readonly SessionCommand[],
  storage: DraftStorage | null = browserStorage(),
): void {
  const store = draftStore(token, storage)
  for (const command of commands) {
    if (command.type === 'profile') store.clearScope(intakeScope(command.stepId))
    else if (command.type === 'note') store.clearField(NOTE_SCOPE, command.garmentId)
  }
}

/**
 * How long a keystroke waits before it is written.
 *
 * Writing on every keystroke is one `localStorage` write, and one JSON round
 * trip of the session's whole record, per character — on the device least able
 * to afford it and while she is typing on it. A short idle window collapses a
 * typed word into one write, and is short enough that nothing meaningful is
 * ever more than a moment from being safe.
 */
const FLUSH_DELAY_MS = 600

/**
 * Keep `entries` for `scope`, off the render path.
 *
 * The timer alone would lose the last few characters when the tab goes away
 * mid-word, which is the exact case this story exists for, so the values are
 * also flushed the moment the page is hidden. `visibilitychange` and
 * `pagehide` are the two events a phone actually delivers — `beforeunload` is
 * not dependable on mobile Safari and is not used.
 *
 * There is deliberately no flush on unmount. A screen unmounts when its
 * command has been accepted and its draft has just been cleared, and a
 * parting write would put back what the acceptance retired. Leaving on
 * Continue therefore drops at most an unflushed instant of typing that the
 * server has already been told about.
 */
export function useDraftFlush(store: DraftStore, scope: string, entries: Readonly<Record<string, string>>): void {
  const latest = useRef({ store, scope, entries })
  useEffect(() => {
    latest.current = { store, scope, entries }
  })

  const flush = useCallback(() => {
    const current = latest.current
    current.store.write(current.scope, current.entries)
  }, [])

  // The entries are rebuilt on every render, so their content — not their
  // identity — is what restarts the timer. Restarting it on an unrelated
  // re-render (a refetch, a pending flag) would postpone the write for as long
  // as the screen keeps rendering.
  const signature = JSON.stringify(entries)

  useEffect(() => {
    if (signature === '{}') return
    const timer = window.setTimeout(flush, FLUSH_DELAY_MS)
    return () => window.clearTimeout(timer)
  }, [signature, flush])

  useEffect(() => {
    const onPageHide = () => flush()
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', onPageHide)
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.removeEventListener('pagehide', onPageHide)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [flush])
}
